import React, { useState, useEffect } from 'react';
import { SPARK_URL } from '../apiConfig';

// ============================================
// AccessibilitySettings
// ============================================
// First-class accessibility configuration.
// Same component pattern works for all Narbodica apps.
// ============================================

const API = SPARK_URL.replace('/api/spark', '/api');

export default function AccessibilitySettings({ onBack }) {
  var [prefs, setPrefs] = useState(null);
  var [loading, setLoading] = useState(true);
  var [saving, setSaving] = useState(false);
  var [saved, setSaved] = useState(false);

  var token = localStorage.getItem('accessToken');

  useEffect(function() { loadPrefs(); }, []);

  async function loadPrefs() {
    setLoading(true);
    try {
      var res = await fetch(API + '/preferences/accessibility', {
        headers: { 'Authorization': 'Bearer ' + token },
      });
      var data = await res.json();
      if (data.success) setPrefs(data.data);
    } catch (e) {}
    setLoading(false);
  }

  async function save(section, value) {
    setSaving(true);
    setSaved(false);
    try {
      var body = { accessibility: {} };
      // Deep set the value
      var parts = section.split('.');
      var current = body.accessibility;
      for (var i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {};
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;

      await fetch(API + '/preferences', {
        method: 'PUT',
        headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      // Update local state
      setPrefs(function(prev) {
        var next = JSON.parse(JSON.stringify(prev || {}));
        var c = next;
        for (var j = 0; j < parts.length - 1; j++) {
          if (!c[parts[j]]) c[parts[j]] = {};
          c = c[parts[j]];
        }
        c[parts[parts.length - 1]] = value;
        return next;
      });
      setSaved(true);
      setTimeout(function() { setSaved(false); }, 2000);
    } catch (e) {}
    setSaving(false);
  }

  var s = {
    container: { padding: '0 16px 100px' },
    section: { marginBottom: 24 },
    sectionTitle: { fontSize: 15, fontWeight: 700, color: '#e4e4e7', margin: '0 0 4px' },
    sectionDesc: { fontSize: 12, color: '#71717a', margin: '0 0 12px', lineHeight: 1.5 },
    row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border-light)' },
    label: { flex: 1 },
    labelText: { fontSize: 14, color: '#e4e4e7', fontWeight: 500 },
    labelDesc: { fontSize: 12, color: '#71717a', marginTop: 2, lineHeight: 1.4 },
    toggle: function(on) { return {
      width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
      background: on ? '#a78bfa' : 'var(--border)',
      position: 'relative', transition: 'background 0.2s', flexShrink: 0,
    }; },
    toggleDot: function(on) { return {
      width: 20, height: 20, borderRadius: '50%', background: '#fff',
      position: 'absolute', top: 2, left: on ? 22 : 2, transition: 'left 0.2s',
    }; },
    input: { width: 80, padding: '8px 10px', fontSize: 14, textAlign: 'center', borderRadius: 8, border: '1px solid #3f3f46', background: '#27272a', color: '#e4e4e7', fontFamily: 'inherit' },
    textarea: { width: '100%', padding: '10px 12px', fontSize: 14, borderRadius: 10, border: '1px solid #3f3f46', background: '#27272a', color: '#e4e4e7', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', minHeight: 80, lineHeight: 1.5 },
  };

  function Toggle({ section, label, desc }) {
    var parts = section.split('.');
    var val = prefs;
    for (var i = 0; i < parts.length; i++) { val = val ? val[parts[i]] : false; }
    return (
      <div style={s.row} role="switch" aria-checked={!!val} aria-label={label}>
        <div style={s.label}>
          <div style={s.labelText}>{label}</div>
          {desc && <div style={s.labelDesc}>{desc}</div>}
        </div>
        <button style={s.toggle(!!val)} onClick={function() { save(section, !val); }}
          aria-label={label + (val ? ' on' : ' off')}>
          <div style={s.toggleDot(!!val)} />
        </button>
      </div>
    );
  }

  function NumberInput({ section, label, desc, placeholder }) {
    var parts = section.split('.');
    var val = prefs;
    for (var i = 0; i < parts.length; i++) { val = val ? val[parts[i]] : null; }
    return (
      <div style={s.row}>
        <div style={s.label}>
          <div style={s.labelText}>{label}</div>
          {desc && <div style={s.labelDesc}>{desc}</div>}
        </div>
        <input type="number" value={val || ''} placeholder={placeholder || ''}
          style={s.input} aria-label={label}
          onChange={function(e) { save(section, e.target.value ? parseInt(e.target.value) : null); }}
        />
      </div>
    );
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: '#71717a' }}>Loading...</div>;
  if (!prefs) return <div style={{ textAlign: 'center', padding: 40, color: '#71717a' }}>Could not load preferences</div>;

  return (
    <div style={s.container}>
      {onBack && (
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#a78bfa', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '12px 0' }}>
          ← Back
        </button>
      )}
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#e4e4e7', margin: '0 0 4px' }}>Accessibility</h2>
      <p style={{ fontSize: 13, color: '#71717a', margin: '0 0 20px', lineHeight: 1.5 }}>
        Make this app work for you. Every setting here adapts the entire experience.
      </p>
      {saved && <div style={{ padding: '8px 14px', borderRadius: 8, background: 'rgba(34,197,94,0.1)', color: '#22c55e', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Saved</div>}

      {/* Vision */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Vision</div>
        <div style={s.sectionDesc}>For blind, low-vision, or colour-blind users</div>
        <Toggle section="vision.blindOrLowVision" label="I'm blind or have low vision" desc="Enables voice-first mode, verbal profile descriptions, and defaults to blind discovery mode" />
        <Toggle section="vision.screenReaderActive" label="I use a screen reader" desc="Optimises all elements for VoiceOver, TalkBack, or other screen readers" />
        <Toggle section="vision.highContrast" label="High contrast" desc="Maximum contrast between text and backgrounds" />
        <NumberInput section="vision.customFontSize" label="Custom font size (px)" desc="Leave blank for default. Try 18-24 for larger text." placeholder="e.g. 20" />
        <div style={s.row}>
          <div style={s.label}>
            <div style={s.labelText}>Colour blindness</div>
            <div style={s.labelDesc}>Adjusts colour choices throughout the app</div>
          </div>
          <select value={prefs.vision?.colourBlindness || 'none'}
            onChange={function(e) { save('vision.colourBlindness', e.target.value); }}
            aria-label="Colour blindness type"
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #3f3f46', background: '#27272a', color: '#e4e4e7', fontSize: 13 }}>
            <option value="none">None</option>
            <option value="protanopia">Protanopia (red-green)</option>
            <option value="deuteranopia">Deuteranopia (red-green)</option>
            <option value="tritanopia">Tritanopia (blue-yellow)</option>
            <option value="achromatopsia">Achromatopsia (no colour)</option>
          </select>
        </div>
      </div>

      {/* Hearing */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Hearing</div>
        <div style={s.sectionDesc}>For deaf or hard-of-hearing users</div>
        <Toggle section="hearing.deafOrHardOfHearing" label="I'm deaf or hard of hearing" desc="Disables voice features, enables visual notifications and captions" />
        <Toggle section="hearing.preferVisualNotifications" label="Visual notifications only" desc="No sound — all notifications are visual" />
        <Toggle section="hearing.flashNotifications" label="Flash screen on notifications" desc="The screen flashes briefly instead of playing a sound" />
        <Toggle section="hearing.captionsAlwaysOn" label="Always show captions" desc="Captions on all video and avatar content" />
      </div>

      {/* Motor */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Motor & mobility</div>
        <div style={s.sectionDesc}>For users with limited hand/arm mobility</div>
        <Toggle section="motor.limitedMobility" label="I have limited mobility" desc="Enables larger buttons, voice commands, and removes swipe requirements" />
        <Toggle section="motor.largerTapTargets" label="Larger buttons and tap targets" desc="All interactive elements become bigger and easier to tap" />
        <NumberInput section="motor.minTapTargetSize" label="Minimum button size (px)" desc="Default is 44px. Try 56-72 for easier tapping." placeholder="44" />
        <Toggle section="motor.disableSwipeGestures" label="Disable swipe gestures" desc="All swipe actions have button alternatives instead" />
        <Toggle section="motor.voiceCommandsEnabled" label="Enable voice commands" desc="Control the app by speaking: 'like this profile', 'pass', 'open messages'" />
      </div>

      {/* Cognitive */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Cognitive</div>
        <div style={s.sectionDesc}>For users who prefer simpler interfaces</div>
        <Toggle section="cognitive.simplifiedInterface" label="Simplified interface" desc="Fewer elements on screen, clearer layout" />
        <Toggle section="cognitive.reducedDensity" label="Reduced information density" desc="Show fewer items per screen with more space between them" />
        <Toggle section="cognitive.dyslexicFont" label="Dyslexic-friendly font" desc="Uses OpenDyslexic font designed for easier reading" />
        <Toggle section="cognitive.extendedTimeouts" label="Extended timeouts" desc="More time before actions expire or screens change" />
      </div>

      {/* Custom needs */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Anything else?</div>
        <div style={s.sectionDesc}>Describe any other needs in your own words. Maia reads this and adapts to help you.</div>
        <textarea style={s.textarea}
          value={prefs.customNeeds || ''}
          placeholder="Tell us anything about how you use your phone or what would make this app easier for you..."
          aria-label="Custom accessibility needs"
          onChange={function(e) { save('customNeeds', e.target.value); }}
        />
      </div>
    </div>
  );
}
